import { useMemo } from 'react'
import {
  environment,
  TreeNodeOrNodeSlug,
  useCheckAccess,
  useCommonTenantLinks,
  useHasLicensedFeature,
} from 'react-common'
import { useTranslation } from 'react-i18next'
import { useIsLearnerDashboardEnabled } from '@/features/dashboard/useIsLearnerDashboardEnabled'
import {
  LEARN_CALENDAR_SLUG,
  LEARN_CATALOG_SLUG,
  LEARN_DASHBOARD_SLUG,
  LEARN_MY_COURSES_SLUG,
} from '@/features/layout/LayoutMain/constants'
import { useCoursesTitle } from '@/hooks/useCoursesTitle'
import { useLearnerNavigationVisibilitySettings } from '@/hooks/useLearnerNavigationVisibilitySettings'

// TODO: after cloud release, this should be removed
export function lmsCloudRedirects() {
  const value = environment().lms_cloud_redirects

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true'
  }
}

// reference MR: https://gitlab.constr.dev/rolos/frontend/-/merge_requests/3241/diffs
function getProjectsUrlConditionally() {
  if (lmsCloudRedirects()) {
    return `/cloud/fragments/research/researchMain?returnUrl=/`
  }

  return '/platform/projects?returnUrl=/'
}

function getKnowledgeModelsUrlConditionally() {
  if (lmsCloudRedirects()) {
    return `/cloud/fragments/knowledge-center/knowledgeCenterMain?returnUrl=/`
  }

  return '/platform/knowledge-models?returnUrl=/'
}

export const useCommonNavigationItems = () => {
  const { t } = useTranslation()

  const learnerNavigationVisibilitySettings = useLearnerNavigationVisibilitySettings()
  const coursesTitle = useCoursesTitle()
  const tenantLinks = useCommonTenantLinks()
  const isDashboardEnabled = useIsLearnerDashboardEnabled()
  const researchProjectsEnabled = useResearchProjectsEnabled()
  const knowledgeModelsEnabled = useKnowledgeModelsEnabled()

  return useMemo(() => {
    const newHamburgerItems: Array<TreeNodeOrNodeSlug> = []

    if (isDashboardEnabled) {
      newHamburgerItems.push({
        url: '/learn',
        name: t('dashboard.heading'),
        icon_id: 'DashboardBoldStroke',
        slug: LEARN_DASHBOARD_SLUG,
      })
    }

    newHamburgerItems.push({
      url: '/learn/courses',
      name: coursesTitle,
      icon_id: 'CourseBoldStroke',
      slug: LEARN_MY_COURSES_SLUG,
    })

    if (learnerNavigationVisibilitySettings.catalog) {
      newHamburgerItems.push({
        url: '/learn/catalog',
        name: t('catalogList.heading'),
        icon_id: 'CatalogBoldStroke',
        slug: LEARN_CATALOG_SLUG,
      })
    }

    if (researchProjectsEnabled) {
      newHamburgerItems.push({
        url: getProjectsUrlConditionally(),
        name: t('researchProjects'),
        icon_id: 'CompositeBoldStroke',
        slug: 'platform-projects',
      })
    }

    if (knowledgeModelsEnabled) {
      newHamburgerItems.push({
        url: getKnowledgeModelsUrlConditionally(),
        name: t('knowledgeModels'),
        icon_id: 'RelationsBoldStroke',
        slug: 'platform-knowledge-models',
      })
    }

    if (learnerNavigationVisibilitySettings.rooms) {
      newHamburgerItems.push({
        url: '/classroom/learner/rooms',
        name: t('roomsList.heading'),
        icon_id: 'CameraVideoBoldStroke',
        slug: 'rooms.list_room',
      })
    }

    if (learnerNavigationVisibilitySettings.calendar) {
      newHamburgerItems.push({
        url: '/learn/calendar',
        name: t('calendar'),
        icon_id: 'CalendarBoldStroke',
        slug: LEARN_CALENDAR_SLUG,
      })
    }

    if (tenantLinks) {
      newHamburgerItems.push(
        ...(tenantLinks.map((i) => ({
          ...i,
          children: i.children ? Array.from(i.children.values()) : undefined,
        })) as TreeNodeOrNodeSlug[]),
      )
    }

    return newHamburgerItems
  }, [
    isDashboardEnabled,
    coursesTitle,
    learnerNavigationVisibilitySettings.catalog,
    learnerNavigationVisibilitySettings.rooms,
    learnerNavigationVisibilitySettings.calendar,
    researchProjectsEnabled,
    knowledgeModelsEnabled,
    tenantLinks,
    t,
  ])
}

const useResearchProjectsEnabled = () => {
  const learnerNavigationVisibilitySettings = useLearnerNavigationVisibilitySettings()
  const { hasAccess } = useCheckAccess({
    requiredPermissions: [{ name: 'Research.Access', scope_type: null, scope_id: null }],
  })

  const hasFeature = useHasLicensedFeature(['Platform.Research'])

  return hasFeature && learnerNavigationVisibilitySettings?.researchProjects && hasAccess
}

const useKnowledgeModelsEnabled = () => {
  const learnerNavigationVisibilitySettings = useLearnerNavigationVisibilitySettings()
  const { hasAccess } = useCheckAccess({
    requiredPermissions: [{ name: 'Research.Access', scope_type: null, scope_id: null }],
  })

  const hasFeature = useHasLicensedFeature(['Platform.KnowledgeModels'])

  return hasFeature && learnerNavigationVisibilitySettings?.knowledgeModels && hasAccess
}
